import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { TUser, UserSchema, UserStatus } from '@/features/users/schemas/users.schema.ts';
import { format } from 'date-fns';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import { Label } from '@/components/ui/label.tsx';
import { getLabelRole, getLabelStatus } from '@/features/users/schemas/users.utils.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { useUpdateUserQuery } from '@/features/users/hooks/mutations/[user]/update-user.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { useRolesQuery } from '@/features/users/hooks/queries/use-roles-query.ts';

export const UsersTableRowEditAction = ({ user }: { user: TUser }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const queryClient = useQueryClient();
  const updateUserMutation = useUpdateUserQuery({
    queryClient,
    onSuccess: () => {
      setIsDialogOpen(false);
    },
  });

  const { data: rolesData } = useRolesQuery();

  const form = useForm({
    resolver: zodResolver(UserSchema),
    defaultValues: user,
  });

  const handleDeviceUpdate = (formData: TUser) => {
    if (!formData) return;

    updateUserMutation.mutate({
      userId: formData.userId,
      status: formData.status,
      role: formData.role,
    });

    // setIsDialogOpen(false);
  };

  useEffect(() => {
    if (user) {
      form.reset(user);
    }
  }, [user, form]);

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <CredenzaTrigger asChild>
        <button className="focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors select-none hover:bg-gray-100 data-disabled:pointer-events-none data-disabled:opacity-50">
          <span>Edit</span>
        </button>
      </CredenzaTrigger>
      <CredenzaContent>
        <Form {...form}>
          <CredenzaHeader>
            <CredenzaTitle>
              Edit Details for {user.firstName} {user.lastName}
            </CredenzaTitle>
          </CredenzaHeader>
          <CredenzaBody>
            <CredenzaDescription className="hidden">
              Edit device details including serial number, type, model, supplier, and dashboard
              visibility settings. Changes will be applied to the selected device in the table.
            </CredenzaDescription>
            <div className="flex flex-col gap-2">
              <FormItem>
                <FormLabel className="text-muted-foreground">User Name</FormLabel>
                <FormControl>
                  <Input value={`${user.firstName} ${user.lastName}`} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
              <FormItem>
                <FormLabel className="text-muted-foreground">Email</FormLabel>
                <FormControl>
                  <Input value={user.email} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <RadioGroup
                        className="grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-4"
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                        }}
                      >
                        {UserStatus.filter((r) =>
                          ['Active', 'Maintenance', 'Suspended', 'Rejected'].includes(r)
                        ).map((userStatus) => (
                          <div key={userStatus} className="flex items-center space-x-2">
                            <RadioGroupItem
                              variant="green"
                              value={userStatus}
                              id={userStatus}
                            />
                            <Label className="cursor-pointer" htmlFor={userStatus}>
                              <Badge variant={getLabelStatus(userStatus)}>{userStatus}</Badge>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <FormControl>
                      <RadioGroup
                        className="grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-4 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]"
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                        }}
                      >
                        {rolesData?.map((role) => (
                          <div key={role.value} className="flex items-center space-x-2">
                            <RadioGroupItem
                              variant="green"
                              value={role.label}
                              id={role.label}
                            />
                            <Label className="cursor-pointer text-nowrap" htmlFor={role.label}>
                              <Badge variant={getLabelRole(role.label)}>{role.label}</Badge>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormItem>
                <FormLabel className="text-muted-foreground">Updated At</FormLabel>
                <FormControl>
                  <Input
                    value={
                      user?.updatedAt
                        ? format(new Date(user?.updatedAt), 'dd/MM/yyyy HH:mm:ss')
                        : ''
                    }
                    disabled
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </div>
          </CredenzaBody>
          <CredenzaFooter>
            <Button
              disabled={updateUserMutation.isPending}
              type="button"
              variant="ghost"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={updateUserMutation.isPending}
              type="button"
              onClick={form.handleSubmit(handleDeviceUpdate)}
            >
              Save Changes
            </Button>
          </CredenzaFooter>
        </Form>
      </CredenzaContent>
    </Credenza>
  );
};
